import { useEffect, useRef } from 'react';

/**
 * Auto-grow a ``<textarea>`` between ``minRows`` and ``maxRows`` based
 * on its content. Pass the current ``value`` so the height recomputes
 * on every keystroke (controlled-input pattern).
 *
 * Used by both compose surfaces (user widget + ``/admin/messages``) so
 * the field starts at one row and expands up to five before scrolling.
 */
export function useAutoGrowTextarea(
  value: string,
  { minRows = 1, maxRows = 5 }: { minRows?: number; maxRows?: number } = {},
) {
  const ref = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    const ta = ref.current;
    if (!ta) return;
    // Reset to ``auto`` so ``scrollHeight`` measures the content's
    // intrinsic height (rather than the current — possibly larger — box).
    ta.style.height = 'auto';
    const computed = window.getComputedStyle(ta);
    const lineHeight = parseFloat(computed.lineHeight || '0') || 20;
    const paddingTop = parseFloat(computed.paddingTop || '0');
    const paddingBottom = parseFloat(computed.paddingBottom || '0');
    const minH = lineHeight * minRows + paddingTop + paddingBottom;
    const maxH = lineHeight * maxRows + paddingTop + paddingBottom;
    const target = Math.min(Math.max(ta.scrollHeight, minH), maxH);
    ta.style.height = `${target}px`;
    ta.style.overflowY = ta.scrollHeight > maxH ? 'auto' : 'hidden';
  }, [value, minRows, maxRows]);

  return ref;
}
